import React from "react";
import { Styled } from "./styles";
import SectionTitle from "@/components/ui/section-title";
import { i18n } from "@/utils/i18n";
import { Report } from "@/types/report";
import { Box, Divider, Typography } from "@mui/material";
import constant from "@/utils/constant";
import useEconomicOverview from "./hooks";
import { formatNegativeAmount } from "@/utils/helpers";

interface Props {
    incomings: Report["incomings"];
    outgoings: Report["outgoings"];
    accounts: Report["accounts"];
    startDate: string;
    endDate: string;
}

const EconomicOverview: React.FC<Props> = ({
    incomings,
    outgoings,
    accounts,
    startDate,
    endDate,
}) => {
    const { CURRENCIES } = constant;

    const { startBalance, endBalance, income, expense, getSafeNumber } = useEconomicOverview({
        incomings,
        outgoings,
        accounts,
    });

    const difference = getSafeNumber(startBalance.plus(income).minus(endBalance.plus(expense)));

    return (
        <Styled.Container>
            <SectionTitle title={i18n.t("economicOverview")} />
            <Box paddingLeft="60px" display="flex" flexDirection="column" gap={4}>
                <Box display="flex" flexDirection="column" gap={1}>
                    <Box display="flex" alignItems="center" gap={10}>
                        <Typography variant="h5" fontWeight="bold">
                            {i18n.t("bankFundsAsOf", { date: startDate })} (summa A)
                        </Typography>
                        <Typography variant="subtitle1">
                            {CURRENCIES.KRONA.format(getSafeNumber(startBalance))}
                        </Typography>
                    </Box>
                    <Box display="flex" alignItems="center" justifyContent="space-between" gap={10}>
                        <Typography variant="h6">
                            {i18n.t("incomeDuringPeriod")} (summa B)
                        </Typography>
                        <Typography variant="subtitle1">
                            + {CURRENCIES.KRONA.format(getSafeNumber(income))}
                        </Typography>
                    </Box>
                    <Box display="flex" alignItems="center" justifyContent="space-between" gap={10}>
                        <Typography variant="h6">
                            {i18n.t("expenseDuringPeriod")} (summa C)
                        </Typography>
                        <Typography variant="subtitle1">
                            {formatNegativeAmount(getSafeNumber(expense))}
                        </Typography>
                    </Box>
                </Box>
                <Box display="flex" alignItems="center" justifyContent="space-between" gap={10}>
                    <Typography variant="h5" fontWeight="bold">
                        {i18n.t("bankFundsAsOf", { date: endDate })} (summa D)
                    </Typography>
                    <Typography variant="subtitle1">
                        {CURRENCIES.KRONA.format(getSafeNumber(endBalance))}
                    </Typography>
                </Box>
                <Divider />
                <Box display="flex" flexDirection="column" gap={1}>
                    <Box display="flex" alignItems="center" justifyContent="space-between" gap={10}>
                        <Typography variant="h5" fontWeight="bold">
                            A + B
                        </Typography>
                        <Typography variant="subtitle1">
                            {CURRENCIES.KRONA.format(getSafeNumber(startBalance.plus(income)))}
                        </Typography>
                    </Box>
                    <Box display="flex" alignItems="center" justifyContent="space-between" gap={10}>
                        <Typography variant="h5" fontWeight="bold">
                            C + D
                        </Typography>
                        <Typography variant="subtitle1">
                            {CURRENCIES.KRONA.format(getSafeNumber(endBalance.plus(expense)))}
                        </Typography>
                    </Box>
                </Box>
                <Box display="flex" alignItems="center" justifyContent="space-between" gap={10}>
                    <Typography variant="h5" fontWeight="bold">
                        {i18n.t("difference")} (A + B = C + D)
                    </Typography>
                    <Typography
                        variant="h5"
                        fontWeight="bold"
                        color={difference < 0 ? "red" : "unset"}
                    >
                        {CURRENCIES.KRONA.format(difference)}
                    </Typography>
                </Box>
            </Box>
        </Styled.Container>
    );
};

export default EconomicOverview;
